(function () {
  'use strict';

  const BASE_PATH = '/apps/personal-center/v1';
  const ABORTED = 'AbortError';
  const ROUTES = Object.freeze({
    '/config': { methods: ['GET'], queryKeys: [] },
    '/me/overview': { methods: ['GET'], queryKeys: [] },
    '/me': { methods: ['GET', 'PATCH'], queryKeys: [] },
    '/me/addresses': { methods: ['GET', 'POST'], queryKeys: [] },
    '/orders': { methods: ['GET'], queryKeys: ['status', 'pageSize', 'cursor', 'search'] },
    '/orders/summary': { methods: ['GET'], queryKeys: [] },
    '/credit/summary': { methods: ['GET'], queryKeys: [] },
    '/credit/balance': { methods: ['GET'], queryKeys: [] },
    '/credit/transactions': { methods: ['GET'], queryKeys: ['pageSize', 'cursor', 'includePending'] },
    '/credit/pending': { methods: ['GET'], queryKeys: [] },
    '/coupons': { methods: ['GET'], queryKeys: ['status'] }
  });

  const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const text = (value) => value == null ? '' : String(value);
  const isAborted = (error) => error?.name === ABORTED;
  const adapterError = (code, message, extra = {}) => Object.assign(new Error(message), { code }, extra);
  const isE164 = (value) => /^\+[1-9]\d{4,14}$/.test(text(value).trim());

  function validatePhone(value) {
    if (value == null || text(value).trim() === '') return;
    if (!isE164(value)) throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El teléfono debe usar el formato internacional E.164.');
  }

  function validateAddress(address) {
    if (!address || typeof address !== 'object' || Array.isArray(address)) {
      throw adapterError('APP_PROXY_INVALID_RESPONSE', 'La dirección no es válida.');
    }
    validatePhone(address.phone);
    const countryCode = text(address.countryCode).trim();
    const provinceCode = text(address.provinceCode).trim();
    if (countryCode && !/^[A-Z]{2,3}$/.test(countryCode)) {
      throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El código de país no es válido.');
    }
    if (provinceCode && !/^[A-Z0-9][A-Z0-9 -]{0,31}$/i.test(provinceCode)) {
      throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El código de estado o provincia no es válido.');
    }
  }

  function resolvePath(path, method) {
    const value = text(path).trim();
    if (!value || !value.startsWith('/') || value.includes('?') || value.includes('#') || value.includes('\\') || value.includes('..')) {
      throw adapterError('APP_PROXY_INVALID_RESPONSE', 'La ruta de cuenta no es válida.');
    }
    let definition = hasOwn(ROUTES, value) ? ROUTES[value] : null;
    if (!definition && /^\/orders\/[^/]+$/.test(value)) definition = { methods: ['GET'], queryKeys: [] };
    if (!definition && /^\/me\/addresses\/[^/]+$/.test(value)) definition = { methods: ['PATCH', 'DELETE'], queryKeys: [] };
    if (definition && definition.methods.includes(method)) return { path: value, queryKeys: definition.queryKeys };
    throw adapterError('APP_PROXY_INVALID_RESPONSE', 'La ruta de cuenta no está autorizada.');
  }

  function toQuery(pathDefinition, query) {
    const params = new URLSearchParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (!pathDefinition.queryKeys.includes(key)) throw adapterError('APP_PROXY_INVALID_RESPONSE', 'La consulta de cuenta no está autorizada.');
      if (value !== null && value !== undefined && value !== '') params.set(key, text(value));
    });
    return params;
  }

  function isErrorEnvelope(payload) {
    return Boolean(payload) && typeof payload === 'object' && payload.success === false && typeof payload.requestId === 'string'
      && payload.error && typeof payload.error === 'object' && typeof payload.error.code === 'string'
      && typeof payload.error.message === 'string'
      && (!hasOwn(payload.error, 'details') || (payload.error.details && typeof payload.error.details === 'object'));
  }

  function isSuccessEnvelope(payload) {
    return Boolean(payload) && typeof payload === 'object' && payload.success === true && typeof payload.requestId === 'string'
      && hasOwn(payload, 'data')
      && (!hasOwn(payload, 'meta') || (payload.meta && typeof payload.meta === 'object'));
  }

  function failureFromEnvelope(payload, status) {
    const extra = { status, requestId: payload.requestId, envelope: payload };
    if (hasOwn(payload.error, 'details')) extra.details = payload.error.details;
    return adapterError(payload.error.code, payload.error.message, extra);
  }

  class AppPersonalCenterAdapter {
    endpoint(path, query, method = 'GET') {
      const definition = resolvePath(path, method);
      const search = toQuery(definition, query).toString();
      return `${BASE_PATH}${definition.path}${search ? `?${search}` : ''}`;
    }

    async request(path, { method = 'GET', query, body, signal } = {}) {
      const normalizedMethod = text(method).trim().toUpperCase() || 'GET';
      const hasBody = body !== undefined;
      if (hasBody && (!body || typeof body !== 'object' || Array.isArray(body) || !['POST', 'PATCH'].includes(normalizedMethod))) {
        throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El cuerpo de la solicitud de cuenta no es válido.');
      }
      if (!hasBody && ['POST', 'PATCH'].includes(normalizedMethod)) {
        throw adapterError('APP_PROXY_INVALID_RESPONSE', 'La solicitud de cuenta necesita datos.');
      }

      const endpoint = this.endpoint(path, query, normalizedMethod);
      const headers = { Accept: 'application/json' };
      if (hasBody) headers['Content-Type'] = 'application/json';
      let response;
      try {
        response = await window.fetch(endpoint, {
          method: normalizedMethod,
          credentials: 'same-origin',
          cache: 'no-store',
          headers,
          body: hasBody ? JSON.stringify(body) : undefined,
          signal
        });
      } catch (error) {
        if (isAborted(error)) throw error;
        throw adapterError('APP_PROXY_UNAVAILABLE', 'No se pudo conectar con el servicio de cuenta.', { cause: error });
      }

      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El servicio de cuenta devolvió una respuesta inválida.', { status: response.status, cause: error });
      }
      if (isErrorEnvelope(payload)) throw failureFromEnvelope(payload, response.status);
      if (!response.ok || !isSuccessEnvelope(payload)) {
        throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El servicio de cuenta devolvió una respuesta inválida.', { status: response.status, envelope: payload });
      }
      return payload;
    }

    getConfig({ signal } = {}) { return this.request('/config', { signal }); }
    getOverview({ signal } = {}) { return this.request('/me/overview', { signal }); }
    getProfile({ signal } = {}) { return this.request('/me', { signal }); }
    updateProfile({ profile, signal } = {}) {
      if (profile && typeof profile === 'object' && !Array.isArray(profile) && hasOwn(profile, 'email')) {
        throw adapterError('APP_PROXY_INVALID_RESPONSE', 'El correo electrónico no se puede cambiar aquí.');
      }
      validatePhone(profile?.phone);
      return this.request('/me', { method: 'PATCH', body: profile, signal });
    }
    listAddresses({ signal } = {}) { return this.request('/me/addresses', { signal }); }
    createAddress({ address, signal } = {}) {
      validateAddress(address);
      return this.request('/me/addresses', { method: 'POST', body: address, signal });
    }
    updateAddress({ addressId, address, signal } = {}) {
      validateAddress(address);
      return this.request(`/me/addresses/${encodeURIComponent(text(addressId))}`, { method: 'PATCH', body: address, signal });
    }
    deleteAddress({ addressId, signal } = {}) { return this.request(`/me/addresses/${encodeURIComponent(text(addressId))}`, { method: 'DELETE', signal }); }
    listOrders({ status, pageSize, cursor, search, signal } = {}) { return this.request('/orders', { query: { status, pageSize, cursor, search }, signal }); }
    getOrderSummary({ signal } = {}) { return this.request('/orders/summary', { signal }); }
    getOrder({ orderId, signal } = {}) { return this.request(`/orders/${encodeURIComponent(text(orderId))}`, { signal }); }
    getCreditSummary({ signal } = {}) { return this.request('/credit/summary', { signal }); }
    getCreditBalance({ signal } = {}) { return this.request('/credit/balance', { signal }); }
    listCreditTransactions({ pageSize, cursor, includePending, signal } = {}) { return this.request('/credit/transactions', { query: { pageSize, cursor, includePending }, signal }); }
    getCreditPending({ signal } = {}) { return this.request('/credit/pending', { signal }); }
    listCoupons({ status, signal } = {}) { return this.request('/coupons', { query: { status }, signal }); }
  }

  window.PersonalCenterApp = window.PersonalCenterApp || {};
  window.PersonalCenterApp.Adapter = AppPersonalCenterAdapter;
})();
